import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Target, Eye, Heart, Users, Award, Globe, Shield, Handshake, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_my-digital-home-7/artifacts/ben2d0xw_Photoroom_20251219_184057.png";

const AboutPage = () => {
  const { t, isRTL } = useLanguage();

  const values = [
    { icon: Shield, ...t('about.values.trust') },
    { icon: Users, ...t('about.values.people') },
    { icon: Award, ...t('about.values.excellence') },
    { icon: Handshake, ...t('about.values.integrity') },
  ];

  const milestones = [
    { year: '2014', ...t('about.timeline.year2014') },
    { year: '2016', ...t('about.timeline.year2016') },
    { year: '2019', ...t('about.timeline.year2019') },
    { year: '2024', ...t('about.timeline.year2024') },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Page Header */}
      <section className="py-16 md:py-24 bg-card">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              {t('about.pageTitle')} <span className="text-gradient-uae">{t('about.pageTitleHighlight')}</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground">
              {t('about.pageSubtitle')}
            </p>
            {/* UAE Flag Accent */}
            <div className="flex justify-center gap-2 mt-8">
              <div className="w-12 h-1.5 bg-uae-red rounded-full" />
              <div className="w-12 h-1.5 bg-uae-green rounded-full" />
              <div className="w-12 h-1.5 bg-foreground rounded-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Company Introduction */}
      <section className="section-padding">
        <div className="container-custom">
          <div className={`grid lg:grid-cols-2 gap-12 items-center ${isRTL ? 'lg:grid-flow-dense' : ''}`}>
            {/* Content */}
            <div className={`space-y-6 ${isRTL ? 'lg:col-start-2 text-right' : ''}`}>
              <div className={`inline-flex items-center gap-2 px-4 py-2 bg-uae-red/10 rounded-full ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Globe className="w-4 h-4 text-uae-red" />
                <span className="text-sm font-medium text-uae-red">{t('about.servingSince')}</span>
              </div>
              
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground">
                TDABYR ALMSTQBL FZE
              </h2>
              <p className="text-lg text-uae-green font-medium font-display">تدابير المستقبل</p>
              
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  <strong className="text-foreground">TDABYR ALMSTQBL FZE</strong> {t('about.companyIntro')}
                </p>
                <p>{t('about.companyIntro2')}</p>
                <p>{t('about.companyIntro3')}</p>
              </div>
            </div>

            {/* Logo & Decorative Element */}
            <div className={`relative ${isRTL ? 'lg:col-start-1' : ''}`}>
              <Card className="bg-muted border-border p-12">
                <div className="flex flex-col items-center">
                  <img
                    src={LOGO_URL}
                    alt="TDABYR ALMSTQBL"
                    className="w-64 h-auto mb-8"
                  />
                  <div className="text-center">
                    <p className="font-display text-2xl font-bold text-foreground mb-2">
                      TDABYR ALMSTQBL FZE
                    </p>
                    <p className="text-muted-foreground">{t('about.yourPartner')}</p>
                  </div>
                  {/* UAE Flag */}
                  <div className="flex gap-1 mt-6">
                    <div className="w-10 h-1.5 bg-uae-red rounded-full" />
                    <div className="w-10 h-1.5 bg-uae-green rounded-full" />
                    <div className="w-10 h-1.5 bg-foreground rounded-full" />
                  </div>
                </div>
              </Card>
              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-uae-red/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-uae-green/10 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-card">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Mission */}
            <Card className="bg-background border-border card-hover">
              <CardContent className={`p-8 md:p-10 ${isRTL ? 'text-right' : ''}`}>
                <div className={`w-14 h-14 rounded-xl bg-uae-red/10 flex items-center justify-center mb-6 ${isRTL ? 'mr-0 ml-auto' : ''}`}>
                  <Target className="w-7 h-7 text-uae-red" />
                </div>
                <h3 className="font-display text-2xl font-bold text-foreground mb-4">{t('about.ourMission')}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {t('about.missionText')}
                </p>
              </CardContent>
            </Card>

            {/* Vision */}
            <Card className="bg-background border-border card-hover">
              <CardContent className={`p-8 md:p-10 ${isRTL ? 'text-right' : ''}`}>
                <div className={`w-14 h-14 rounded-xl bg-uae-green/10 flex items-center justify-center mb-6 ${isRTL ? 'mr-0 ml-auto' : ''}`}>
                  <Eye className="w-7 h-7 text-uae-green" />
                </div>
                <h3 className="font-display text-2xl font-bold text-foreground mb-4">{t('about.ourVision')}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {t('about.visionText')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('about.coreValues')} <span className="text-uae-red">{t('about.coreValuesHighlight')}</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('about.coreValuesSubtitle')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="bg-card border-border card-hover group">
                <CardContent className={`p-6 text-center ${isRTL ? 'text-right' : ''}`}>
                  <div className={`w-14 h-14 mx-auto rounded-xl bg-muted flex items-center justify-center mb-4 group-hover:bg-uae-green/10 transition-colors ${isRTL ? 'mx-0 mr-auto ml-0' : ''}`}>
                    <value.icon className="w-7 h-7 text-uae-green" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                    {value.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-padding bg-card">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('about.ourJourney')} <span className="text-uae-green">{t('about.ourJourneyHighlight')}</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('about.ourJourneySubtitle')}
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="relative">
              {/* Timeline Line */}
              <div className={`absolute top-0 bottom-0 w-0.5 bg-border ${isRTL ? 'right-8' : 'left-8'}`} />

              {/* Timeline Items */}
              <div className="space-y-8">
                {milestones.map((milestone, index) => (
                  <div key={milestone.year} className={`relative flex gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    {/* Year Dot */}
                    <div className="relative z-10 flex-shrink-0">
                      <div className={`w-16 h-16 rounded-full ${index % 2 === 0 ? 'bg-uae-red' : 'bg-uae-green'} flex items-center justify-center`}>
                        <span className="text-uae-white font-bold text-sm">{milestone.year}</span>
                      </div>
                    </div>

                    {/* Content */}
                    <Card className="bg-background border-border flex-grow">
                      <CardContent className={`p-6 ${isRTL ? 'text-right' : ''}`}>
                        <h4 className="font-display text-lg font-semibold text-foreground mb-2">
                          {milestone.event}
                        </h4>
                        <p className="text-muted-foreground text-sm">
                          {milestone.description}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-uae-red to-uae-green">
        <div className="container-custom text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-uae-white mb-4">
            {t('about.readyToWork')}
          </h2>
          <p className="text-uae-white/80 mb-8 max-w-2xl mx-auto">
            {t('about.readyToWorkSubtitle')}
          </p>
          <Button
            variant="default"
            size="xl"
            className="bg-uae-white text-uae-black hover:bg-uae-white/90 gap-2"
            asChild
          >
            <Link to="/contact">
              {t('about.getInTouch')}
              <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
